import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import Link from '@mui/material/Link';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemAvatar from '@mui/material/ListItemAvatar';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Pagination from '@mui/material/Pagination';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';

import { PartyPersonType, type Person } from '@tyro/api';
import {
  Avatar,
  PlaceholderCard,
  PreferredNameFormat,
  SearchInput,
  usePaginationList,
  usePreferredNameLayout,
} from '@tyro/core';
import { TrashIcon } from '@tyro/icons';
import { type ReactNode, useCallback, useMemo, useRef, useState } from 'react';

export type PersonPagination = Omit<Person, '__typename'> & {
  customName?: string;
  caption?: string;
};

type ListPeoplePaginationProps<T extends PersonPagination> = {
  people: T[];
  emptyTitle?: string;
  emptyDescription?: string;
  noFoundMessage: string;
  removeLabel: string;
  atLeastOneRequired?: string;
  rightAdornment?: ReactNode;
  renderAction?: (person: T) => ReactNode;
  onFocus?: () => void;
  onRowSelection?: (person: T[]) => void;
  onRemove: (partyId: T['partyId']) => void;
};

export const ListPeoplePagination = <T extends PersonPagination>({
  people,
  emptyTitle,
  emptyDescription,
  noFoundMessage,
  removeLabel,
  atLeastOneRequired,
  rightAdornment,
  renderAction,
  onRowSelection,
  onFocus,
  onRemove,
}: ListPeoplePaginationProps<T>) => {
  const { displayName, searchDisplayName } = usePreferredNameLayout();
  const [searchPeople, setSearchPeople] = useState('');

  const selectedPeopleRef = useRef<Record<number, T>>({} as Record<number, T>);

  const filteredPeople = useMemo(
    () => searchDisplayName(people, searchPeople),
    [people, searchPeople],
  );

  const { currentList, paginationCount, currentPage, setCurrentPage } =
    usePaginationList<T[]>({
      initialList: people,
      filteredList: filteredPeople,
    });

  const handleRowSelection = useCallback((checked: boolean, person: T) => {
    if (checked) {
      selectedPeopleRef.current[person.partyId] = person;
    } else {
      delete selectedPeopleRef.current[person.partyId];
    }

    onRowSelection?.(Object.values(selectedPeopleRef.current));
  }, []);

  return (
    <Grid container gap={2}>
      {people.length > 0 && (
        <Grid size={{ xs: 12 }}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            gap={2}
          >
            <SearchInput
              value={searchPeople}
              onChange={(e) => setSearchPeople(e.target.value)}
              size="small"
              containerProps={{ width: rightAdornment ? '50%' : '100%' }}
            />
            {rightAdornment}
          </Stack>
        </Grid>
      )}
      <Grid size={{ xs: 12 }}>
        {people.length === 0 && (emptyTitle || emptyDescription) && (
          <PlaceholderCard cardProps={{ sx: { boxShadow: 'none', p: 0 } }}>
            <Box>
              {emptyTitle && (
                <Typography component="h4" variant="subtitle1">
                  {emptyTitle}
                </Typography>
              )}
              {emptyDescription && (
                <Link
                  component="button"
                  type="button"
                  variant="body2"
                  onClick={onFocus}
                >
                  {emptyDescription}
                </Link>
              )}
            </Box>
          </PlaceholderCard>
        )}
        {filteredPeople.length === 0 && searchPeople.length > 0 && (
          <PlaceholderCard
            cardProps={{
              sx: { boxShadow: 'none', p: 0 },
            }}
          >
            <Stack direction="column">
              <Typography component="h4" variant="body1" color="primary">
                {noFoundMessage}
              </Typography>
              <img
                alt=""
                src="/assets/illustrations/illustration-user-cloud.svg"
              />
            </Stack>
          </PlaceholderCard>
        )}
        {filteredPeople.length > 0 && (
          <List sx={{ width: '100%' }}>
            {currentList.map((person) => {
              const isDisabledRemoveButton =
                !!atLeastOneRequired && currentList.length === 1;
              const name = person.type
                ? displayName(person)
                : displayName(
                    { ...person, type: PartyPersonType.Student },
                    { format: PreferredNameFormat.FirstnameSurname },
                  );

              return (
                <ListItem
                  key={person?.partyId}
                  secondaryAction={
                    <Tooltip
                      title={
                        isDisabledRemoveButton
                          ? atLeastOneRequired
                          : removeLabel
                      }
                    >
                      <span>
                        <IconButton
                          aria-label={removeLabel}
                          disabled={isDisabledRemoveButton}
                          color="primary"
                          onClick={() => onRemove(person?.partyId)}
                        >
                          <TrashIcon />
                        </IconButton>
                      </span>
                    </Tooltip>
                  }
                >
                  {onRowSelection && (
                    <ListItemIcon>
                      <Checkbox
                        edge="start"
                        tabIndex={-1}
                        checked={!!selectedPeopleRef.current[person?.partyId]}
                        disableRipple
                        onChange={(_event, checked) => {
                          handleRowSelection(checked, person);
                        }}
                      />
                    </ListItemIcon>
                  )}
                  <ListItemAvatar>
                    <Avatar
                      src={person?.avatarUrl}
                      person={person.type ? person : undefined}
                      name={name}
                    />
                  </ListItemAvatar>
                  <ListItemText
                    primary={person?.customName ?? name}
                    secondary={person?.caption}
                  />
                  {renderAction?.(person)}
                </ListItem>
              );
            })}
          </List>
        )}
      </Grid>
      {paginationCount > 1 && (
        <Grid size={{ xs: 12 }}>
          <Pagination
            page={currentPage}
            onChange={(_e, p) => setCurrentPage(p)}
            count={paginationCount}
          />
        </Grid>
      )}
    </Grid>
  );
};
